import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Lock, Download, RefreshCw, Mail, Calendar, LogOut, Users, Vote, Crown, Trophy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'waybig_admin_key';

const FLAVOR_LABELS = {
  coffee: { name: 'Coffee', emoji: '☕' },
  matcha: { name: 'Matcha', emoji: '🍵' },
  cheesecake: { name: 'Cheesecake', emoji: '🍰' },
  'red-velvet': { name: 'Red Velvet', emoji: '❤️' },
};

const AdminPage = () => {
  const [adminKey, setAdminKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('leads');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [voteData, setVoteData] = useState(null);
  const [referrers, setReferrers] = useState([]);

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      setAdminKey(saved);
      verifyAndLoad(saved);
    }
  }, []);

  const verifyAndLoad = async (key) => {
    setLoading(true);
    setAuthError('');
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { 'X-Admin-Key': key } });
      sessionStorage.setItem(STORAGE_KEY, key);
      setAuthed(true);
      await Promise.all([loadEntries(key), loadVotes(key), loadReferrers(key)]);
    } catch (err) {
      setAuthError(err.response?.status === 401 ? 'Wrong password. Try again.' : 'Connection error.');
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const loadEntries = async (key) => {
    try {
      const res = await axios.get(`${API}/admin/waitlist`, { headers: { 'X-Admin-Key': key } });
      setEntries(res.data.entries);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to load entries', err);
    }
  };

  const loadVotes = async (key) => {
    try {
      const res = await axios.get(`${API}/admin/votes`, { headers: { 'X-Admin-Key': key } });
      setVoteData(res.data);
    } catch (err) {
      console.error('Failed to load votes', err);
    }
  };

  const loadReferrers = async (key) => {
    try {
      const res = await axios.get(`${API}/admin/referrals`, { headers: { 'X-Admin-Key': key } });
      setReferrers(res.data.top_referrers || []);
    } catch (err) {
      console.error('Failed to load referrers', err);
    }
  };

  const refreshAll = async () => {
    setLoading(true);
    await Promise.all([loadEntries(adminKey), loadVotes(adminKey), loadReferrers(adminKey)]);
    setLoading(false);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    verifyAndLoad(adminKey);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setAdminKey('');
    setEntries([]);
    setVoteData(null);
    setReferrers([]);
    setTotal(0);
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist/export`, {
        headers: { 'X-Admin-Key': adminKey },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `waybig-waitlist-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="font-['Bebas_Neue'] text-3xl text-white">WAY BIG</Link>
            <p className="font-['Outfit'] text-xs uppercase tracking-[0.3em] text-[#FFB300] mt-2">Admin Dashboard</p>
          </div>

          <div className="border border-[#2A2A2A] bg-black/40 p-10">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="text-[#FF3D00]" size={20} />
              <h1 className="font-['Bebas_Neue'] text-2xl text-white uppercase">Restricted Access</h1>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <input
                data-testid="admin-password-input"
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Admin password"
                required
                autoFocus
                className="w-full bg-black border-2 border-[#2A2A2A] focus:border-[#FF3D00] text-white font-['Manrope'] px-4 py-3 outline-none"
              />
              <button
                data-testid="admin-login-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF3D00] hover:bg-[#FFB300] text-black font-['Bebas_Neue'] text-xl uppercase tracking-wider py-3 px-6 border-2 border-[#FF3D00] hover:border-[#FFB300] disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Enter'}
              </button>
              {authError && (
                <p data-testid="admin-auth-error" className="text-[#FF3D00] text-sm text-center font-['Manrope']">{authError}</p>
              )}
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  const todayCount = entries.filter(e => {
    if (!e.timestamp) return false;
    const d = new Date(e.timestamp);
    const today = new Date();
    return d.toDateString() === today.toDateString();
  }).length;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-[#2A2A2A] px-6 py-4 sticky top-0 bg-black/95 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-['Bebas_Neue'] text-2xl text-white">WAY BIG</Link>
            <span className="font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300] hidden sm:inline">· Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="admin-refresh-all-button"
              onClick={refreshAll}
              disabled={loading}
              className="flex items-center gap-2 text-white/60 hover:text-[#FF3D00] font-['Outfit'] uppercase text-xs tracking-wider"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 text-white/60 hover:text-[#FF3D00] font-['Outfit'] uppercase text-xs tracking-wider"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <h1 className="font-['Bebas_Neue'] text-5xl sm:text-6xl text-white uppercase mb-2" data-testid="admin-heading">
            Dashboard
          </h1>
          <p className="font-['Manrope'] text-white/60">Everything you need. Updates live.</p>
        </motion.div>

        {/* Top stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <div className="border border-[#2A2A2A] bg-black/40 p-6">
            <div className="text-[#FFB300] font-['Outfit'] text-xs uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
              <Users size={12} /> Total Signups
            </div>
            <div className="font-['Bebas_Neue'] text-4xl text-white" data-testid="stat-total">{total.toLocaleString()}</div>
          </div>
          <div className="border border-[#2A2A2A] bg-black/40 p-6">
            <div className="text-[#FFB300] font-['Outfit'] text-xs uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
              <Calendar size={12} /> Today
            </div>
            <div className="font-['Bebas_Neue'] text-4xl text-white" data-testid="stat-today">{todayCount}</div>
          </div>
          <div className="border border-[#2A2A2A] bg-black/40 p-6">
            <div className="text-[#FFB300] font-['Outfit'] text-xs uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
              <Vote size={12} /> Real Votes
            </div>
            <div className="font-['Bebas_Neue'] text-4xl text-white" data-testid="stat-votes">{voteData?.total_real_votes || 0}</div>
          </div>
          <div className="border border-[#2A2A2A] bg-black/40 p-6">
            <div className="text-[#FFB300] font-['Outfit'] text-xs uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
              <Crown size={12} /> Founders
            </div>
            <div className="font-['Bebas_Neue'] text-4xl text-white" data-testid="stat-founders">
              {referrers.filter(r => r.is_founding_member).length}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#2A2A2A] overflow-x-auto">
          {[
            { id: 'leads', label: 'Leads', icon: Mail },
            { id: 'votes', label: 'Flavor Votes', icon: Vote },
            { id: 'referrers', label: 'Top Referrers', icon: Trophy },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 font-['Bebas_Neue'] text-lg uppercase tracking-wider whitespace-nowrap transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-[#FF3D00] border-[#FF3D00]'
                    : 'text-white/60 border-transparent hover:text-white'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {activeTab === 'leads' && (
          <div className="border border-[#2A2A2A] bg-black/40 overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-[#2A2A2A]">
              <h2 className="font-['Bebas_Neue'] text-xl text-white uppercase tracking-wider">All Leads</h2>
              <button
                data-testid="admin-export-csv-button"
                onClick={handleExport}
                disabled={total === 0}
                className="flex items-center gap-2 bg-[#FF3D00] hover:bg-[#FFB300] text-black font-['Bebas_Neue'] text-sm uppercase tracking-wider py-2 px-4 disabled:opacity-50"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>

            {entries.length === 0 ? (
              <div className="py-16 text-center" data-testid="admin-empty-state">
                <Mail className="mx-auto text-white/30 mb-3" size={32} />
                <p className="text-white/40 font-['Manrope']">No signups yet. Share the link!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-leads-table">
                  <thead className="bg-black/60 border-b border-[#2A2A2A]">
                    <tr>
                      <th className="text-left px-6 py-3 font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">#</th>
                      <th className="text-left px-6 py-3 font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">Name</th>
                      <th className="text-left px-6 py-3 font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">Email</th>
                      <th className="text-left px-6 py-3 font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">Signed Up</th>
                      <th className="text-left px-6 py-3 font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">Ref Code</th>
                      <th className="text-left px-6 py-3 font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">Referred By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, index) => (
                      <tr key={entry.id} className="border-b border-[#1A1A1A] hover:bg-[#FF3D00]/5 transition-colors">
                        <td className="px-6 py-4 font-['Manrope'] text-white/40 text-sm">{total - index}</td>
                        <td className="px-6 py-4 font-['Manrope'] text-white">
                          {entry.name || <span className="text-white/30 italic text-sm">—</span>}
                        </td>
                        <td className="px-6 py-4 font-['Manrope'] text-white">
                          <a href={`mailto:${entry.email}`} className="hover:text-[#FF3D00] transition-colors">
                            {entry.email}
                          </a>
                        </td>
                        <td className="px-6 py-4 font-['Manrope'] text-white/60 text-sm">{formatDate(entry.timestamp)}</td>
                        <td className="px-6 py-4 font-['Manrope'] text-white/70 text-xs">
                          {entry.referral_code || '—'}
                        </td>
                        <td className="px-6 py-4 font-['Manrope'] text-white/60 text-xs">
                          {entry.referred_by ? (
                            <span className="text-[#FFB300]">{entry.referred_by}</span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'votes' && (
          <div className="border border-[#2A2A2A] bg-black/40 p-8" data-testid="votes-panel">
            <div className="mb-6">
              <h2 className="font-['Bebas_Neue'] text-2xl text-white uppercase tracking-wider mb-1">Next Flavor Votes</h2>
              <p className="font-['Manrope'] text-sm text-white/60">
                Real votes from website visitors. Base counts are seeded for social proof.
              </p>
            </div>

            {!voteData ? (
              <p className="text-white/40 font-['Manrope']">Loading...</p>
            ) : (
              <div className="space-y-4">
                {voteData.breakdown.map((item, index) => {
                  const flavor = FLAVOR_LABELS[item.flavor] || { name: item.flavor, emoji: '·' };
                  const totalDisplayed = voteData.breakdown.reduce((s, b) => s + b.displayed_total, 0);
                  const percent = totalDisplayed > 0 ? Math.round((item.displayed_total / totalDisplayed) * 100) : 0;
                  const isLeader = index === 0;

                  return (
                    <div key={item.flavor} className="border border-[#2A2A2A] bg-black/60 p-5" data-testid={`vote-row-${item.flavor}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{flavor.emoji}</span>
                          <div>
                            <h3 className="font-['Bebas_Neue'] text-2xl text-white uppercase tracking-tight">{flavor.name}</h3>
                            {isLeader && (
                              <span className="text-[#FFB300] font-['Outfit'] text-[10px] uppercase tracking-[0.2em] flex items-center gap-1">
                                <Crown size={10} /> Leading
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-['Bebas_Neue'] text-3xl text-[#FF3D00]">{percent}%</div>
                          <div className="font-['Manrope'] text-xs text-white/50">{item.displayed_total.toLocaleString()} total</div>
                        </div>
                      </div>

                      <div className="h-2 bg-white/10 overflow-hidden mb-3">
                        <motion.div
                          className="h-full bg-[#FF3D00]"
                          initial={{ width: 0 }}
                          animate={{ width: `${percent}%` }}
                          transition={{ duration: 1, delay: index * 0.1, ease: 'easeOut' }}
                        />
                      </div>

                      <div className="flex justify-between text-xs font-['Manrope'] text-white/50">
                        <span>Real votes: <span className="text-white">{item.real_votes}</span></span>
                        <span>Seeded base: {item.base_votes}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'referrers' && (
          <div className="border border-[#2A2A2A] bg-black/40 overflow-hidden" data-testid="referrers-panel">
            <div className="px-6 py-4 border-b border-[#2A2A2A]">
              <h2 className="font-['Bebas_Neue'] text-xl text-white uppercase tracking-wider">Top Referrers</h2>
              <p className="font-['Manrope'] text-sm text-white/60 mt-1">3+ referrals = Founding Member status</p>
            </div>

            {referrers.length === 0 ? (
              <div className="py-16 text-center">
                <Trophy className="mx-auto text-white/30 mb-3" size={32} />
                <p className="text-white/40 font-['Manrope']">No referrals yet. Once people start sharing, you'll see them here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-black/60 border-b border-[#2A2A2A]">
                    <tr>
                      <th className="text-left px-6 py-3 font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">Rank</th>
                      <th className="text-left px-6 py-3 font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">Email</th>
                      <th className="text-left px-6 py-3 font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">Code</th>
                      <th className="text-left px-6 py-3 font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">Referrals</th>
                      <th className="text-left px-6 py-3 font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrers.map((r, index) => (
                      <tr key={r.referral_code} className="border-b border-[#1A1A1A] hover:bg-[#FF3D00]/5">
                        <td className="px-6 py-4 font-['Bebas_Neue'] text-xl text-white">#{index + 1}</td>
                        <td className="px-6 py-4 font-['Manrope'] text-white">
                          <a href={`mailto:${r.email}`} className="hover:text-[#FF3D00]">{r.email}</a>
                        </td>
                        <td className="px-6 py-4 font-['Manrope'] text-white/70 text-xs">{r.referral_code}</td>
                        <td className="px-6 py-4 font-['Bebas_Neue'] text-2xl text-[#FF3D00]">{r.referrals_count}</td>
                        <td className="px-6 py-4">
                          {r.is_founding_member ? (
                            <span className="inline-flex items-center gap-1 bg-[#FFB300] text-black px-2 py-1 font-['Outfit'] font-bold text-[10px] uppercase tracking-wider">
                              <Crown size={10} /> Founder
                            </span>
                          ) : (
                            <span className="text-white/40 font-['Manrope'] text-xs">{3 - r.referrals_count} to go</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
